'use client'

// Shared "Visit us" trust surface: where the clinic is, when it is open and
// every approved contact channel. All values come from clinic-identity.ts —
// never hard-code contact details here. Reused on /about and /book; a future
// dedicated contact route can mount this component as-is.

import React from 'react'
import Link from 'next/link'
import { Clock, Mail, MapPin, MessageCircle, Phone } from 'lucide-react'
import { trackAnalyticsEvent } from '@/lib/analytics'
import { clinicIdentity } from '@/lib/clinic-identity'

export default function VisitUs({
  showBookCta = true,
}: {
  /** Hide the booking link where the page already offers booking controls. */
  showBookCta?: boolean
}) {
  const { address, contact, maps, hours } = clinicIdentity

  return (
    <section
      aria-labelledby="visit-us-heading"
      className="mx-auto w-full max-w-5xl px-5 py-10 sm:px-6 sm:py-14 lg:px-8"
    >
      <h2
        id="visit-us-heading"
        className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl"
      >
        Visit {clinicIdentity.name}
      </h2>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:mt-8 sm:grid-cols-2 sm:gap-6">
        {/* Where */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <MapPin
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
            />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Where</h3>
              <address className="mt-1 text-sm not-italic leading-relaxed text-slate-700">
                {address.line1}, {address.line2},
                <br />
                {address.area}, {address.locality},
                <br />
                {address.city}, {address.region} {address.postalCode},{' '}
                {address.country}
              </address>
              <a
                href={maps.placeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block text-sm font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                onClick={() =>
                  trackAnalyticsEvent('directions_click', {
                    cta_location: 'visit_us',
                  })
                }
              >
                Get directions
              </a>
            </div>
          </div>
        </div>

        {/* When */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <Clock
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
            />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Opening hours
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-700">
                {hours.display}
              </p>
              <p className="text-sm leading-relaxed text-slate-700">
                Sunday: {hours.sunday}
              </p>
            </div>
          </div>
        </div>

        {/* Call */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <Phone
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
            />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Call us</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-700">
                <a
                  href={contact.phone.href}
                  className="underline underline-offset-2 hover:text-emerald-800"
                  onClick={() =>
                    trackAnalyticsEvent('phone_click', {
                      cta_location: 'visit_us',
                    })
                  }
                >
                  {contact.phone.display}
                </a>
              </p>
              <p className="text-sm leading-relaxed text-slate-700">
                Landline:{' '}
                <a
                  href={contact.landline.href}
                  className="underline underline-offset-2 hover:text-emerald-800"
                  onClick={() =>
                    trackAnalyticsEvent('phone_click', {
                      cta_location: 'visit_us',
                    })
                  }
                >
                  {contact.landline.display}
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Message / email */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <MessageCircle
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
            />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Message us
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-700">
                <a
                  href={contact.whatsapp.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-emerald-800"
                  onClick={() =>
                    trackAnalyticsEvent('whatsapp_click', {
                      cta_location: 'visit_us',
                    })
                  }
                >
                  WhatsApp {contact.whatsapp.display}
                </a>
              </p>
              <p className="flex items-center gap-1.5 text-sm leading-relaxed text-slate-700">
                <Mail aria-hidden="true" className="h-4 w-4 text-emerald-600" />
                <a
                  href={contact.publicEmail.href}
                  className="break-all underline underline-offset-2 hover:text-emerald-800"
                  onClick={() =>
                    trackAnalyticsEvent('email_click', {
                      cta_location: 'visit_us',
                    })
                  }
                >
                  {contact.publicEmail.display}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {showBookCta && (
        <p className="mt-6 text-sm text-slate-700 sm:mt-8">
          Ready to take the next step?{' '}
          <Link
            href="/book"
            className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
          >
            Book an appointment
          </Link>{' '}
          or{' '}
          <Link
            href="/team"
            className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
          >
            meet our team
          </Link>
          .
        </p>
      )}
    </section>
  )
}
